import { useCallback, useEffect, useState } from 'react';
import { Popover } from 'react-tiny-popover';

import { SocialMediaPopover } from '@/components/SocialMediaPopover';
import { useEnvironment } from '@/cutils/context/EnvironmentContext';
import { COMMENT_BUTTON_ID } from '@/cutils/hooks/useComments';
import { createShareUrl } from '@/cutils/shareurls/createShareUrl';

import { useCommentsCount } from './CommentsCount/use-comments-count';
import styles from './SocialMediaSharer.module.scss';
import { createShareText } from './SocialMediaSharer.utils';
import { Count } from './base/Count/Count';
import { IconSystemButton } from './base/IconButton/IconSystemButton';
import { IconSystemLink } from './base/IconButton/IconSystemLink';

type Props = {
	direction: 'horizontal' | 'vertical';
	headline?: string | null;
	url: string;
	className?: string;
	commentId?: string;
};

export function SocialMediaSharer({ className = '', direction, headline, url, commentId }: Props) {
	const environment = useEnvironment();
	const [popOverVisible, setPopOverVisible] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const commentCount = useCommentsCount(commentId);

	const isHorizontal = direction === 'horizontal';
	const mailShareUrl = createShareUrl('email', createShareText('email', headline), 'E-Mail', url);

	useEffect(() => {
		if (navigator.userAgent.match(/(iPad|iPhone|iPod|android)/i)) {
			setIsMobile(true);
		}
	}, []);

	/**
	 * Toggles the visibility of the popover based on device type and share availability.
	 *
	 */
	const togglePopOver = useCallback(async () => {
		let sharingUrl: string;
		try {
			const urlObject = new URL(url);
			urlObject.searchParams.set('UTM_Name', 'Web-Share');
			urlObject.searchParams.set('UTM_Source', 'Link');
			urlObject.searchParams.set('UTM_Medium', 'Link');
			sharingUrl = urlObject.toString();
		} catch (e) {
			sharingUrl = url;
		}

		const shareData = {
			text: headline ? `${headline} | via BR24` : 'Via BR24',
			url: sharingUrl,
		};

		if (isMobile && navigator.canShare?.(shareData)) {
			try {
				await navigator.share(shareData);
			} catch (e) {
				// do not handle any failure
			}
		} else {
			setPopOverVisible(!popOverVisible);
		}
	}, [headline, isMobile, popOverVisible, url]);

	return (
		<div className={`${styles.wrapper} ${className ? className : ''} ${isHorizontal ? styles.isWrapperHorizontal : ''}`}>
			{typeof commentId === 'string' && !environment.flags.IS_COMMENTS_HIDDEN && (
				<IconSystemLink
					type="secondary"
					icon="comment"
					variant="filled"
					title="Kommentare anzeigen"
					className={styles.commentButton}
					href={{ hash: COMMENT_BUTTON_ID }}
				>
					{commentCount !== undefined && commentCount !== 0 ? (
						<Count className={styles.commentsCount} count={commentCount} aria-label="Anzahl Kommentare" />
					) : null}
				</IconSystemLink>
			)}
			<IconSystemLink
				target="_self"
				href={mailShareUrl.replaceAll(/\+/g, '%20')}
				type="secondary"
				icon="mail"
				variant="filled"
				title="Per Mail teilen"
			/>
			<Popover
				isOpen={popOverVisible}
				positions={['bottom', 'right']}
				boundaryInset={20}
				reposition={true}
				onClickOutside={() => setPopOverVisible(false)}
				content={({ position, nudgedLeft, nudgedTop, popoverRect }) => {
					const arrowPositionLeft = popoverRect.width / 2 - nudgedLeft;
					const arrowPositionTop = popoverRect.height / 2 - nudgedTop;

					return (
						<SocialMediaPopover
							headline={headline}
							url={url}
							onClose={() => setPopOverVisible(false)}
							position={position}
							arrowPositionLeft={arrowPositionLeft}
							arrowPositionTop={arrowPositionTop}
						/>
					);
				}}
			>
				<IconSystemButton type="secondary" icon="share" variant="filled" title="Teilen" onClick={togglePopOver} />
			</Popover>
		</div>
	);
}
